'use client';

import React, { useEffect, useState } from 'react';
import DigitalCardLayerStyled from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/index.styled';
import type { TextLayer } from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/types';
import { useDigitalCardConfig, useDigitalCardState } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';

import TextLayerStyled from './index.styled';

const DEFAULT_SCALE = 1;
const DEFAULT_DELAY = 0.2;

export interface Shadow {
  xOffset: number;
  yOffset: number;
  blur: number;
  color: string;
}

export interface Font {
  fontFamily: string;
  color: string;
  fontSize: number;
  fontWeight: number;
  lineHeight?: number;
  shadow?: Shadow[];
}

export interface DigitalCardText {
  content: string;
  font: Font;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  width?: number;
  textAlign?: 'left' | 'center' | 'right';
}

/**
 * [텍스트 레이어 컴포넌트]
 *
 * @description
 * 텍스트 레이어를 렌더링하는 컴포넌트입니다.
 *
 * @param {TextLayer} props - 텍스트 레이어 컴포넌트의 속성
 * ```ts
 * {
 *   idx: number; // 레이어의 고유 식별자
 *   name: string; // 레이어의 이름
 *   type: 'text'; // 레이어 타입
 *   zIndex: number; // 레이어의 z-index 값
 *   translateZ: number; // 레이어의 z축 변환 값
 *   borderRadius: number; // 레이어의 모서리 둥글기
 *   text: {
 *     content: string; // 텍스트 내용
 *     font: {
 *       fontFamily: string; // 폰트 패밀리
 *       color: string; // 텍스트 색상
 *       fontSize: number; // 폰트 크기
 *       fontWeight: number; // 폰트 두께
 *       lineHeight?: number; // 행간
 *       shadow?: { // 그림자 효과 (없을 시 미적용, 배열로 여러개 적용 가능)
 *         xOffset: number; // x축 그림자 오프셋
 *         yOffset: number; // y축 그림자 오프셋
 *         blur: number; // 그림자 블러
 *         color: string; // 그림자 색상
 *       }[];
 *     };
 *     top?: number; // 텍스트 위치
 *     bottom?: number; // 텍스트 위치
 *     left?: number; // 텍스트 위치
 *     right?: number; // 텍스트 위치
 *   }[];
 *   animation?: 'fadeIn' | 'fadeInLeft' | 'fadeInRight' | 'fadeInUp' | 'fadeInDown' | 'scale'; // 텍스트 효과 타입 (없을 시 미적용)
 * }
 * ```
 */
const DigitalCardTextLayer = ({ zIndex, translateZ, borderRadius, text, animation }: TextLayer) => {
  const { width } = useDigitalCardConfig();
  const { cardRef, isAllResourcesLoaded } = useDigitalCardState();
  const [scale, setScale] = useState(DEFAULT_SCALE);

  let charIndex = 0;

  /**
   * 문자 렌더링
   */
  const renderChars = (text: string, color: string, shadow?: Shadow[]) => {
    return text.split('').map((char, index) => {
      const currentIndex = charIndex++;
      const delay = `${currentIndex * DEFAULT_DELAY}s`;

      return (
        <TextLayerStyled.CharWrapper key={index}>
          {/* 그림자는 shadow 관련 속성이 있을 때만 적용 */}
          {shadow && (
            <TextLayerStyled.ShadowChar
              $shadow={shadow}
              $animation={animation}
              style={{ animationDelay: animation ? delay : undefined }}
            >
              {char}
            </TextLayerStyled.ShadowChar>
          )}
          <TextLayerStyled.MainChar
            $color={color}
            $animation={animation}
            style={{ animationDelay: animation ? delay : undefined }}
          >
            {char}
          </TextLayerStyled.MainChar>
        </TextLayerStyled.CharWrapper>
      );
    });
  };

  /**
   * 카드 크기에 따른 스케일링 계산
   */
  useEffect(() => {
    const calculateScale = () => {
      if (cardRef?.current?.clientWidth) {
        setScale(cardRef?.current?.clientWidth / width);
      }
    };

    // ResizeObserver 생성
    const resizeObserver = new ResizeObserver(calculateScale);

    // cardRef가 존재하면 observe 시작
    if (cardRef?.current) {
      resizeObserver.observe(cardRef.current);
    }

    // cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [width]);

  if (!isAllResourcesLoaded) {
    return null;
  }

  return (
    <DigitalCardLayerStyled.BaseLayerWrapper $zIndex={zIndex} $translateZ={translateZ} $borderRadius={borderRadius}>
      {text.map((line, index) => (
        <TextLayerStyled.TextBase
          key={index}
          $scale={scale}
          $fontFamily={line.font.fontFamily}
          $fontWeight={line.font.fontWeight}
          $fontSize={line.font.fontSize}
          $lineHeight={line.font.lineHeight}
          $top={line.top}
          $bottom={line.bottom}
          $left={line.left}
          $right={line.right}
          $width={line.width}
          $textAlign={line.textAlign}
        >
          {renderChars(line.content, line.font.color, line.font.shadow)}
        </TextLayerStyled.TextBase>
      ))}
    </DigitalCardLayerStyled.BaseLayerWrapper>
  );
};

export default DigitalCardTextLayer;
